import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import "./style/theme.css";
import App from "./App";
import { AppContextProvider } from "./context/AppStore";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { isEvmHostname } from "./evm/site";

// Note: intentionally NOT wrapped in <React.StrictMode>. Its dev-only double
// mounting makes @solana/wallet-adapter connect/disconnect twice, which
// surfaces as flaky reconnect behaviour.
const tree = (
  <ErrorBoundary>
    <AppContextProvider>
      <App />
    </AppContextProvider>
  </ErrorBoundary>
);

// A prerendered page (scripts/prerender.js) arrives with its content already
// in #root: hydrate it in place. A fresh render (createRoot) would throw
// that markup away, show the lazy route's "Loading Solstack…" placeholder
// until the route's chunk arrived, then draw the page again — a blank flash
// on every docs, blog, academy and tools page, and a late largest paint.
// The app hosts serve an empty shell, which renders from scratch as before,
// and so do the public /launch pages: the Worker fills those with its own
// HTML (worker/launch.js), not React's, so there's nothing to hydrate.
const container = document.getElementById("root");
const prerendered = container.firstElementChild && !/^\/launch(\/|$)/.test(window.location.pathname);
if (isEvmHostname(window.location.hostname)) {
  // The EVM Dust Cleaner's host asks for seed phrases, so the main app never
  // runs there, even when the asset server hands back the 404 page for a
  // path that skips the Worker (worker/evm.js serves its own page itself).
  document.title = "Not found";
  container.textContent = "Not found";
} else if (prerendered) {
  hydrateRoot(container, tree);
} else {
  createRoot(container).render(tree);
}
